import { ExclamationCircleOutlined } from "@ant-design/icons"
import { CurrencyEnum, RightEnum, VendorTypeEnum, WorkflowConditionEnum } from "@finway-group/shared/lib/models"
import { Button, Col, Form, Row, Select, Tooltip } from "antd"
import React, { useEffect } from "react"
import { Plus as PlusIcon } from "react-feather"
import { useTranslation } from "react-i18next"

import CurrencyInput from "Components/currencyInput"
import FormItemCurrency from "Components/form/formItemCurrency"
import { TrashIcon } from "Components/icons/TrashIcon"
import { ALL_TRIGGER_OBJECTS } from "Shared/config/consts"
import { useCostCenters2 } from "Shared/hooks/costCenter2.hooks"
import { useSubCostCenters } from "Shared/hooks/costCenter.hooks"
import { useEmployees, useLoggedInEmployee } from "Shared/hooks/employee.hooks"
import { useUserCreditors } from "Shared/hooks/vendor.hooks"
import { defaultQueryParams } from "Shared/queries/vendors/consts"
import { useVendorList } from "Shared/queries/vendors/vendor.queries"
import { AuthzService } from "Shared/services"
import { getTooltipPopupContainer } from "Shared/utils/helper.utils"
import { getPopupAnchor } from "Shared/utils/popup.utils"

import { CurrencySelect } from "../expenseForm/commonInputFields/CurrencySelect"
import { VendorSelect } from "../expenseForm/commonInputFields/VendorSelect"
import { WorkflowFormResourceTypeEnum, WorkflowTriggerObjectSelect } from "./triggerObjectSelect"
import { WorkflowFormRequestTypeEnum, WorkflowFormTriggerType, WorkflowFormTypeEnum, WorkflowFormValuesInterface } from "./workflowFormValues.interface"

interface WorkflowFormTriggerTypesInterface {
    formValues: WorkflowFormValuesInterface
    setFormValues: (formUpdate: Partial<WorkflowFormValuesInterface>) => void
    rules: any
}

const DEFAULT_TRIGGER_CONDITIONS = Object.values(WorkflowConditionEnum).filter((condition) => condition !== WorkflowConditionEnum.EXPENSE_KIND)
const FOLDER_ALLOWED_TRIGGER_CONDITIONS = [WorkflowConditionEnum.COST_CENTER, WorkflowConditionEnum.COST_CENTER_2, WorkflowConditionEnum.REQUESTER]
const PER_DIEM_AND_MILEAGE_ALLOWED_TRIGGER_CONDITIONS = [
    WorkflowConditionEnum.COST_CENTER,
    WorkflowConditionEnum.COST_CENTER_2,
    WorkflowConditionEnum.REQUESTER,
    WorkflowConditionEnum.NET_AMOUNT,
]

// prettier-ignore
const areOnlyPerDiemOrMileageOrBothSelected = (requestTypes: Array<WorkflowFormRequestTypeEnum>) => {
    if (!requestTypes.length) {return false}
    const includesSelectedTypes = requestTypes.filter(
        (type) => type === WorkflowFormRequestTypeEnum.PER_DIEM_REIMBURSEMENT || type === WorkflowFormRequestTypeEnum.MILEAGE_REIMBURSEMENT,
    ).length
    return includesSelectedTypes === requestTypes.length
}

const areOnlyStandardOrHospitalityOrBothSelected = (requestTypes: Array<WorkflowFormRequestTypeEnum>) => {
    if (!requestTypes.length) {
        return false
    }

    const includesSelectedTypes = requestTypes.filter(
        (type) => type === WorkflowFormRequestTypeEnum.STANDARD_REIMBURSEMENT || type === WorkflowFormRequestTypeEnum.HOSPITALITY_REIMBURSEMENT,
    ).length

    return includesSelectedTypes === requestTypes.length
}

const getConditionOptions = (workflowType: WorkflowFormTypeEnum, requestTypes: Array<WorkflowFormRequestTypeEnum>) => {
    if (workflowType === WorkflowFormTypeEnum.FOLDER) {
        return FOLDER_ALLOWED_TRIGGER_CONDITIONS
    }
    if (areOnlyPerDiemOrMileageOrBothSelected(requestTypes)) {
        return PER_DIEM_AND_MILEAGE_ALLOWED_TRIGGER_CONDITIONS
    }
    return DEFAULT_TRIGGER_CONDITIONS
}

export const WorkflowFormTriggerTypes = ({ formValues, setFormValues, rules }: WorkflowFormTriggerTypesInterface) => {
    const { t } = useTranslation()
    const costCenters = useSubCostCenters()
    const userCreditors = useUserCreditors(AuthzService.isRightGrantedForLoggedInUser(RightEnum.EMPLOYEE__ALL__READ))
    const { data: vendorCreditors } = useVendorList({ ...defaultQueryParams, limit: 1 }) // limit to 1 as we are interested only whether there are any vendors or not
    const employees = useEmployees()
    const costCenters2 = useCostCenters2()
    const isFolderWorkflow = formValues.workflowType === WorkflowFormTypeEnum.FOLDER
    const loggedInEmployee = useLoggedInEmployee()

    // On triggerType=request, mileage & per diem do not support the VENDOR option. If only those two are selected, remove VENDOR from the trigger options.
    useEffect(() => {
        const onlyPerDiemAndOrMileageAreSelected = areOnlyPerDiemOrMileageOrBothSelected(formValues.requestTypes)
        if (onlyPerDiemAndOrMileageAreSelected) {
            const triggerTypes = formValues.triggerTypes.filter((triggerType) => triggerType.condition !== WorkflowConditionEnum.VENDOR)
            if (!triggerTypes.length) {
                triggerTypes.push({ condition: WorkflowConditionEnum.COST_CENTER })
            }
            setFormValues({ triggerTypes })
        }
    }, [formValues.requestTypes.length])

    const handleTriggerTypeConditionSelect = (index: number, condition: WorkflowConditionEnum) => {
        const triggerTypes = [...formValues.triggerTypes]
        triggerTypes[index].referenceIds = []
        triggerTypes[index].condition = condition
        if (triggerTypes[index].condition === WorkflowConditionEnum.NET_AMOUNT) {
            triggerTypes[index].currency = loggedInEmployee.settings.globalCurrency ?? CurrencyEnum.EUR
        }
        setFormValues({ triggerTypes })
    }

    const handleTriggerTypeReferenceSelect = (index: number, referenceIds: any) => {
        const triggerTypes = [...formValues.triggerTypes]
        triggerTypes[index].referenceIds = referenceIds
        setFormValues({ triggerTypes })
    }

    const addTriggerType = () => {
        const conditionsAlreadyInTheForm = formValues.triggerTypes.map((triggerType) => triggerType.condition)
        const conditions = getConditionOptions(formValues.workflowType, formValues.requestTypes)
        const condition = conditions.filter((condition) => !conditionsAlreadyInTheForm.includes(condition))[0]
        const newTriggerType: WorkflowFormTriggerType = { condition }
        if (newTriggerType.condition === WorkflowConditionEnum.NET_AMOUNT) {
            newTriggerType.currency = loggedInEmployee.settings.globalCurrency ?? CurrencyEnum.EUR
        }
        const triggerTypes = [...formValues.triggerTypes, newTriggerType]
        setFormValues({ triggerTypes })
    }

    const removeTriggerType = (index: number) => {
        const triggerTypes = formValues.triggerTypes.slice(0, index).concat(formValues.triggerTypes.slice(index + 1))
        setFormValues({ triggerTypes })
    }

    const renderResourceTypeSelect = (resourceType: WorkflowFormResourceTypeEnum, triggerType: any, index: number, resources: Array<any>) => {
        if (resourceType) {
            return resourceType === WorkflowFormResourceTypeEnum.VENDOR ? (
                <VendorSelect
                    shouldShowOnlyVerifiedVendors={false}
                    shouldRenderSelectDropdown={false}
                    selectedVendorType={VendorTypeEnum.VENDOR}
                    userCreditors={userCreditors}
                    fallback={String(t("input:filter_form.all"))}
                    fallbackValue={ALL_TRIGGER_OBJECTS}
                    isForm={true}
                    formProps={{ label: t("input:workflow.trigger.vendor"), name: ["triggerTypes", index, "referenceIds"] }}
                    onSelectMultiple={(value: Array<string>) => handleTriggerTypeReferenceSelect(index, value)}
                    mode="multiple"
                    selectedCreditorIds={formValues.triggerTypes[index].referenceIds}
                />
            ) : (
                <WorkflowTriggerObjectSelect
                    name={["triggerTypes", index, "referenceIds"]}
                    label={t(`input:workflow.trigger.${(triggerType.condition as any)?.toLowerCase()}`)}
                    required={true}
                    isMultiple
                    resources={resources}
                    resourceType={resourceType}
                    selectedResourcesIds={triggerType.referenceIds || []}
                    anchor={getPopupAnchor()}
                    onSelect={(value) => handleTriggerTypeReferenceSelect(index, value)}
                    rules={rules.triggerType.referenceIds}
                    includeAllOption
                    data-testid="finway:workflow-trigger:select"
                />
            )
        }

        return null
    }

    const renderTriggerTypeRow = (triggerType: WorkflowFormValuesInterface["triggerTypes"][0], index: number) => {
        let resources = []
        let resourceType

        switch (triggerType.condition) {
            case WorkflowConditionEnum.VENDOR:
                resourceType = WorkflowFormResourceTypeEnum.VENDOR
                break
            case WorkflowConditionEnum.REQUESTER:
                resources = employees
                resourceType = WorkflowFormResourceTypeEnum.EMPLOYEE
                break
            case WorkflowConditionEnum.COST_CENTER_2:
                resources = costCenters2
                resourceType = WorkflowFormResourceTypeEnum.COST_CENTER_2
                break
            case WorkflowConditionEnum.COST_CENTER:
            default:
                resources = costCenters
                resourceType = WorkflowFormResourceTypeEnum.COST_CENTER
        }

        const onlyPerDiemAndOrMileageAreSelected = areOnlyPerDiemOrMileageOrBothSelected(formValues.requestTypes)

        const hasCostCentersOption =
            costCenters.length > 0 &&
            (formValues.triggerTypes[index].condition === WorkflowConditionEnum.COST_CENTER ||
                !formValues.triggerTypes.map((triggerType) => triggerType.condition).includes(WorkflowConditionEnum.COST_CENTER))
        const hasVendorOption =
            vendorCreditors?.totalDocs !== 0 &&
            userCreditors.length !== 0 &&
            (formValues.triggerTypes[index].condition === WorkflowConditionEnum.VENDOR ||
                !formValues.triggerTypes.map((triggerType) => triggerType.condition).includes(WorkflowConditionEnum.VENDOR)) &&
            !onlyPerDiemAndOrMileageAreSelected
        const hasRequestersOption =
            employees.length > 0 &&
            (formValues.triggerTypes[index].condition === WorkflowConditionEnum.REQUESTER ||
                !formValues.triggerTypes.map((triggerType) => triggerType.condition).includes(WorkflowConditionEnum.REQUESTER))
        const hasCostCenters2Option =
            costCenters.length > 0 &&
            (formValues.triggerTypes[index].condition === WorkflowConditionEnum.COST_CENTER_2 ||
                !formValues.triggerTypes.map((triggerType) => triggerType.condition).includes(WorkflowConditionEnum.COST_CENTER_2))
        const hasNetAmountOption =
            formValues.triggerTypes[index].condition === WorkflowConditionEnum.NET_AMOUNT ||
            !formValues.triggerTypes.map((triggerType) => triggerType.condition).includes(WorkflowConditionEnum.NET_AMOUNT)

        return (
            <Row gutter={[16, 16]} key={index} className="items-center">
                <Col span={10}>
                    <Form.Item
                        label={
                            <>
                                <p>{t("input:workflow.trigger.trigger_type")}</p>
                                {[
                                    WorkflowFormRequestTypeEnum.ALL,
                                    WorkflowFormRequestTypeEnum.ALL_REIMBURSEMENTS,
                                    WorkflowFormRequestTypeEnum.PER_DIEM_REIMBURSEMENT,
                                    WorkflowFormRequestTypeEnum.MILEAGE_REIMBURSEMENT,
                                ].some((type) => formValues.requestTypes.includes(type)) &&
                                    formValues.triggerTypes[index].condition === WorkflowConditionEnum.VENDOR && (
                                        <Tooltip
                                            getPopupContainer={getTooltipPopupContainer}
                                            title={t("tooltips:vendor_trigger_type_do_not_apply_to_mileage_and_per_diem")}
                                            placement="right"
                                            className="align-middle"
                                        >
                                            <ExclamationCircleOutlined className="ml-6" />
                                        </Tooltip>
                                    )}
                            </>
                        }
                        name={["triggerTypes", index, "condition"]}
                        rules={rules.triggerType.condition}
                    >
                        <Select
                            data-testid="finway:workflow-trigger:select"
                            getPopupContainer={getPopupAnchor()}
                            defaultValue={WorkflowConditionEnum.VENDOR}
                            onChange={(value) => handleTriggerTypeConditionSelect(index, value)}
                        >
                            {hasCostCentersOption && (
                                <Select.Option value={WorkflowConditionEnum.COST_CENTER} data-testid="finway:workflow-trigger:cost-center">
                                    {t("input:workflow.trigger.cost_center")}
                                </Select.Option>
                            )}
                            {hasCostCenters2Option && (
                                <Select.Option value={WorkflowConditionEnum.COST_CENTER_2} data-testid="finway:workflow-trigger:cost-center-2">
                                    {t("input:workflow.trigger.cost_center_2")}
                                </Select.Option>
                            )}
                            {hasVendorOption && !isFolderWorkflow && (
                                <Select.Option value={WorkflowConditionEnum.VENDOR} data-testid="finway:workflow-trigger:vendor">
                                    {t("input:workflow.trigger.vendor")}
                                </Select.Option>
                            )}
                            {hasRequestersOption && (
                                <Select.Option value={WorkflowConditionEnum.REQUESTER} data-testid="finway:workflow-trigger:requester">
                                    {t("input:workflow.trigger.requester")}
                                </Select.Option>
                            )}
                            {hasNetAmountOption && !isFolderWorkflow && (
                                <Select.Option value={WorkflowConditionEnum.NET_AMOUNT} data-testid="finway:workflow-trigger:net-amount">
                                    {t("input:workflow.trigger.amount")}
                                </Select.Option>
                            )}
                        </Select>
                    </Form.Item>
                </Col>

                {triggerType.condition === WorkflowConditionEnum.NET_AMOUNT ? (
                    <Col span={7}>
                        <FormItemCurrency
                            currency={triggerType.currency || CurrencyEnum.EUR}
                            label={t(`input:workflow.trigger.amount_greater`)}
                            name={["triggerTypes", index, "amount"]}
                            required
                            rules={rules.triggerType.amount}
                        >
                            <CurrencyInput className="react-currency-input w-200" isNegativeAllowed={false} value={0} currency={triggerType.currency || CurrencyEnum.EUR} />
                        </FormItemCurrency>
                    </Col>
                ) : (
                    <Col flex="auto">{resourceType && renderResourceTypeSelect(resourceType, triggerType, index, resources)}</Col>
                )}

                {triggerType.condition === WorkflowConditionEnum.NET_AMOUNT && (
                    <Col flex="auto" sm="24" md="24">
                        <CurrencySelect
                            name={["triggerTypes", index, "currency"]}
                            rules={rules.triggerType.currency}
                            initialValue={formValues.triggerTypes[index].currency}
                            size="small"
                        />
                    </Col>
                )}

                {index !== 0 && (
                    <Col className="w-50 flex justify-end">
                        <Button onClick={() => removeTriggerType(index)}>
                            <TrashIcon />
                        </Button>
                    </Col>
                )}
            </Row>
        )
    }

    return (
        <>
            {formValues.triggerTypes.map((condition: any, index: number) => renderTriggerTypeRow(condition, index))}
            <Row gutter={[16, 16]}>
                {formValues.triggerTypes.length < getConditionOptions(formValues.workflowType, formValues.requestTypes).length && (
                    <Button type={"default"} icon={<PlusIcon />} onClick={addTriggerType}>
                        {t("input:workflow.add_trigger_type")}
                    </Button>
                )}
            </Row>
        </>
    )
}
